/**
 * PhotoGallery — grid of PhotoThumb components for a place's photos.
 * Per UX §3.11: 1px hairline border, no drop shadows, click → lightbox.
 *
 * Uses `usePhotoSignedGet` to fetch a signed R2 URL per photo (15-min TTL).
 */
import * as React from 'react';
import type { PhotoId } from '@shared/types';
import { PhotoThumb } from './ui/PhotoThumb';
import { PhotoLightbox } from './PhotoLightbox';
import { usePhotoUrl } from '../lib/queries';
import { useActiveHouseholdId } from '../lib/household';

interface ThumbProps {
  photoId: PhotoId;
  caption?: string | null;
  onSelect: (photoId: PhotoId) => void;
}

function GalleryThumb({ photoId, caption, onSelect }: ThumbProps) {
  const householdId = useActiveHouseholdId();
  const q = usePhotoUrl(householdId, photoId);
  return (
    <button
      type="button"
      onClick={() => onSelect(photoId)}
      className="block group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent"
      aria-label={caption ?? 'View photo'}
    >
      <PhotoThumb
        src={q.data?.url ?? null}
        alt={caption ?? ''}
        size={96}
      />
    </button>
  );
}

export interface PhotoGalleryProps {
  photoIds: PhotoId[];
  /** Display N most-recent thumbs; "See all (N)" caption when more exist. */
  limit?: number;
  /** Allow the user to see them all (opens the gallery in lightbox mode). */
  onSeeAll?: () => void;
}

export function PhotoGallery({
  photoIds,
  limit,
  onSeeAll,
}: PhotoGalleryProps) {
  const [active, setActive] = React.useState<PhotoId | null>(null);
  const visible =
    limit && photoIds.length > limit ? photoIds.slice(0, limit) : photoIds;
  const remaining =
    limit && photoIds.length > limit ? photoIds.length - limit : 0;

  if (!photoIds.length) {
    return (
      <p className="font-serif italic text-sm text-ink-500">
        No photos yet.
      </p>
    );
  }

  const currentIndex = active ? photoIds.indexOf(active) : -1;

  function prev() {
    if (currentIndex <= 0) return;
    setActive(photoIds[currentIndex - 1]!);
  }
  function next() {
    if (currentIndex < 0 || currentIndex >= photoIds.length - 1) return;
    setActive(photoIds[currentIndex + 1]!);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3" data-testid="photo-gallery-grid">
        {visible.map((id) => (
          <GalleryThumb
            key={id}
            photoId={id}
            onSelect={setActive}
          />
        ))}
      </div>
      {remaining > 0 && onSeeAll ? (
        <button
          type="button"
          onClick={onSeeAll}
          className="mt-4 text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
        >
          See all ({photoIds.length})
        </button>
      ) : null}
      {active ? (
        <PhotoLightbox
          photoId={active}
          onClose={() => setActive(null)}
          onPrev={currentIndex > 0 ? prev : undefined}
          onNext={
            currentIndex >= 0 && currentIndex < photoIds.length - 1
              ? next
              : undefined
          }
        />
      ) : null}
    </div>
  );
}
